import React, { useRef } from "react"
import FlexPaymentsPreview from "./FlexPaymentsPreview"
import moment from "moment"
import multiple from "../../assets/images/multiple.svg"
import { Badge } from "../ui/badge"
import { Button } from "../ui/button"
import { formatTextWithBreaks } from "../../utils/services/service-content-formatter"
import { Label } from "../ui/label"
import { pluralize } from "../../utils/pluralize"
import { Switch } from "../ui/switch"
import {
  SvgBundle,
  SvgCheckCircle,
  SvgConsultation,
  SvgEdit,
  SvgPiechartOneQuarter,
  SvgRecurring,
  SvgStarBox,
  SvgUserCheck,
} from "../icons"

interface Props {
  setShowPreview: any
  serviceValues: any
  setServiceValues: any
  saveType: string
  callback: any
  groupCallback: any
  previewImgUrl: string
  serviceType: string
  isGroupService: boolean
}

const ServicePreview: React.FC<Props> = ({
  setShowPreview,
  serviceValues,
  setServiceValues,
  saveType,
  callback,
  previewImgUrl,
  serviceType,
  isGroupService,
  groupCallback,
}) => {
  const ref = useRef(null)

  const getCostTrailer = () => {
    if (["consultation", "individual"].includes(serviceType)) {
      return " / per session"
    } else if (serviceType === "recurring") {
      const periodString =
        serviceValues?.recurring.recurringFrequency === "weekly"
          ? "week"
          : "month"
      return ` / per ${periodString}`
    } else {
      return " / for program"
    }
  }

  const getCost = () => {
    if (["consultation", "individual"].includes(serviceType)) {
      return serviceValues.sessionCost
    } else if (serviceType === "bundle") {
      return serviceValues.package.packageCost
    } else if (serviceType === "recurring") {
      return serviceValues.recurring.periodCost
    }
  }

  const getType = () => {
    type ResProps = {
      image: React.ReactNode | string
      string: string
    }
    let result: ResProps = { image: "", string: "" }

    if (["consultation"].includes(serviceType)) {
      result = { image: <SvgConsultation />, string: "Consultation" }
    } else if (["individual"].includes(serviceType)) {
      result = { image: <SvgUserCheck />, string: "Individual" }
    } else if (["recurring"].includes(serviceType)) {
      result = { image: <SvgRecurring />, string: "Recurring" }
    } else if (["bundle"].includes(serviceType)) {
      result = { image: <SvgBundle />, string: "Bundle" }
    } else if (["group"].includes(serviceType)) {
      result = { image: multiple, string: "Group Program" }
    }
    return result
  }

  const getSessionCount = () => {
    if (["consultation", "individual"].includes(serviceType)) {
      return "1 session"
    } else if (["bundle"].includes(serviceType)) {
      return pluralize(serviceValues?.package?.packageSessions, "session")
    } else if (["recurring"].includes(serviceType)) {
      return (
        <div>
          {pluralize(serviceValues?.recurring?.sessionsPerPeriod, "session")}
          <span className="text-darkGray">{getCostTrailer()}</span>
        </div>
      )
    } else {
      return "Not Found"
    }
  }

  const getStartDates = () => {
    const meetingDates = serviceValues?.group?.meetingDates || []
    return meetingDates.map((date: any) => {
      const formattedDate = moment(date).format("MMMM Do, YYYY [at] h:mm A")
      return formattedDate
    })
  }

  const handleClick = () => {
    if (isGroupService) {
      groupCallback()
    } else {
      callback()
    }
  }

  const cost = getCost()
  const isCostZero = cost === 0

  const weekOptions = [
    { label: "First", value: 0 },
    { label: "Second", value: 1 },
    { label: "Third", value: 2 },
    { label: "Fourth", value: 3 },
    { label: "Last", value: 4 },
  ]

  const fullWeekdays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ]

  const paymentAmount =
    serviceType === "bundle"
      ? serviceValues?.package?.packageCost
      : isGroupService
      ? serviceValues?.sessionCost
      : serviceValues?.sessionCost

  const decodeUnicodeSymbol = (unicodeStr: string | undefined | null) => {
    if (!unicodeStr) {
      return ""
    }
    return unicodeStr.replace(/\\u([\dA-F]{4})/gi, (match, grp) => {
      return String.fromCharCode(parseInt(grp, 16))
    })
  }

  return (
    <div className="zoee-backdrop-filter fixed left-0 top-0 z-[1008] flex h-screen w-screen items-center justify-center">
      <div
        ref={ref}
        className="main-shadow relative flex h-screen w-screen flex-col justify-between bg-white px-[24px] py-[24px] md:h-auto md:max-h-[90%] md:w-[1280px] md:max-w-[90%] md:flex-row md:rounded-[16px] md:py-[40px] md:pl-[64px] md:pr-[40px]"
      >
        <div className="border-r-grayMist flex h-[70vh] flex-col overflow-y-auto overflow-y-auto md:h-auto md:w-[800px] md:border-r-[1px] md:pr-[64px]">
          <div
            className={`mb-[32px] w-full aspect-[3/1] rounded-[14px] ${
              previewImgUrl ? "bg-white overflow-hidden" : serviceValues.color
            }`}
          >
            {previewImgUrl && (
              <img
                className="h-full w-full object-cover"
                src={previewImgUrl}
                alt="service preview"
              />
            )}
          </div>
          <div className="mb-[17px] -mt-2 flex w-full justify-between flex-row md:items-center">
            <h2 className="text-[18px] font-bold md:text-[22px]">
              {serviceValues.title}
            </h2>
            <div className="text-blurple text-[28px] font-semibold w-[30%] text-end">
              {serviceType === "consultation" &&
              serviceValues?.consultation?.isFree ? (
                <>Free</>
              ) : (
                <>
                  {decodeUnicodeSymbol(serviceValues.symbol)}
                  {getCost()}
                  <span className="text-graySlate text-base">
                    {getCostTrailer()}
                  </span>
                </>
              )}
            </div>
          </div>

          <div className="">
            <div className="bg-grayMist mb-[9px] h-[1px] w-full" />
            <div className="mb-[5] flex flex-row items-center justify-between gap-[3px] text-base font-bold p-5">
              {isGroupService ? (
                <>
                  <img src={multiple} alt="multiple user icon" />
                  <span className="mr-auto">Group Program</span>
                </>
              ) : (
                <>
                  <span className="mr-auto flex-row">
                    {getType().image && (
                      <div className="flex items-center gap-[4px]">
                        {getType().image}
                        {`${getType().string}`}
                      </div>
                    )}
                    {!getType().image && `${getType().string}`}
                  </span>
                </>
              )}

              <div className="mb-[5] flex flex-col items-start gap-2 lg:flex-row lg:items-center text-base font-bold">
                {" "}
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-[11px] w-full lg:w-auto">
                  <div className="bg-grayFlash flex w-full md:w-auto items-center justify-center rounded-[8px] px-[8px] py-[4px] text-base font-bold text-black">
                    {getSessionCount()}
                  </div>

                  <div className="bg-grayFlash flex w-full md:w-auto items-center justify-center rounded-[8px] px-[8px] py-[4px] text-base font-bold text-black">
                    <div>
                      {parseInt(serviceValues?.sessionLength?.minutes)} minutes
                      <span className="text-darkGray"> / session</span>
                    </div>
                  </div>

                  {serviceValues.payment_term === "flexible" && (
                    <div className="bg-grayFlash flex w-full md:w-auto items-center justify-center rounded-[8px] px-[8px] py-[4px] text-base font-bold text-black">
                      Payment Plan Available
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Program Details */}
            <div className="bg-grayFlash mt-3 flex h-[32px] w-[142px] items-center justify-center rounded-[8px]">
              Program Details
            </div>
            <br />
            <p className="mb-4 gap-[12px] text-base space-y-4">
              {formatTextWithBreaks(serviceValues.description)}
            </p>

            {/* Features If Value Exists */}
            <div className="flex items-center gap-[12px] mt-6">
              {serviceValues?.feature1 && (
                <>
                  <SvgCheckCircle />
                  {serviceValues.feature1}
                </>
              )}
            </div>
            <div className="flex items-center gap-[12px]">
              {serviceValues?.feature2 && (
                <>
                  <SvgCheckCircle />
                  {serviceValues.feature2}
                </>
              )}
            </div>
            <div className="flex items-center gap-[12px]">
              {serviceValues?.feature3 && (
                <>
                  <SvgCheckCircle />
                  {serviceValues.feature3}
                </>
              )}
            </div>
          </div>
          <div className="mb-[32px] mr-auto flex flex-col gap-[12px] text-base w-full">
            {isGroupService && (
              <div className="mr-auto flex flex-col mb-[12px] text-base w-full">
                <div className="flex items-center gap-[12px]">
                  <SvgCheckCircle />
                  Group Program
                </div>
                <div className="flex text-start items-center gap-[12px] mb-[12px]">
                  <SvgCheckCircle />
                  Maximum Clients: {serviceValues?.group?.maximumClients}
                </div>
                <div className="bg-grayMist mb-[16px] h-[1px] w-full">
                  &nbsp;
                </div>
                <div className="w-full">
                  {serviceValues?.expiration?.expires ? (
                    <>
                      <div
                        className="bg-grayFlash mb-[10px] flex h-[32px] 
                    w-[180px] items-center justify-center rounded-[8px] font-base"
                      >
                        Recurring Schedule
                      </div>
                      <div className="grid grid-cols-2 gap-2 w-full">
                        {serviceValues?.group?.weeklyDayTimes?.map(
                          (
                            entry: { day: number; time: string; freq: number },
                            idx: number
                          ) => {
                            const freqLabel =
                              weekOptions.find(
                                (opt) => opt.value === entry.freq
                              )?.label || ""
                            const dayLabel = fullWeekdays[entry.day] || ""
                            const timeLabel = moment(
                              entry.time,
                              "HH:mm"
                            ).format("h:mm A")

                            return (
                              <div key={idx} className="text-base">
                                {["monthly", "month", "months"].includes(
                                  serviceValues.expiration?.expireType || ""
                                ) && `${freqLabel} `}
                                {dayLabel} at {timeLabel}
                              </div>
                            )
                          }
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div
                        className="bg-grayFlash mb-[10px] flex h-[32px] 
          w-[142px] items-center justify-center rounded-[8px] font-base"
                      >
                        Scheduled Dates
                      </div>
                      <div className="grid grid-cols-2 gap-2 w-full">
                        {getStartDates().map((date: string, index: number) => (
                          <div key={index} className="flex justify-start">
                            <Badge variant="secondary" className="w-auto">
                              {date}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          {!isCostZero && (
            <div className="bg-grayMist mb-[16px] h-[1px] w-full">&nbsp;</div>
          )}
          {serviceType === "consultation" && !isCostZero && (
            <div className="border-1 border-gray mt-2 flex w-full flex-col rounded-[16px] border p-[20px]">
              <p className="text-graySlate mb-[32px] text-base">
                Payment Details
              </p>
              <div className="mb-[16px] flex w-full items-center justify-between">
                <div className="flex min-w-fit items-center gap-[16px]">
                  <div className="bg-blurple flex h-[24px] w-[24px] items-center justify-center rounded-full font-bold text-white">
                    $
                  </div>
                  <p className="text-[16px]">Payment at checkout</p>
                </div>
                <div className="border-grayCloud mx-[24px] h-2 w-full -translate-y-1 transform border-b border-dashed"></div>
                <span className="text-base">
                  {decodeUnicodeSymbol(serviceValues.symbol)}
                  {serviceType === "consultation" &&
                  serviceValues?.consultation?.isFree
                    ? 0.0
                    : serviceValues?.sessionCost?.toFixed(2)}
                </span>
              </div>
              <div className="text-graySlate bg-grayMist flex w-full items-center justify-center rounded-[12px] p-[26px] text-base">
                <p className="text-center">
                  Payment will be charged{" "}
                  <strong className="text-black">immediately</strong> at
                  checkout.
                </p>
              </div>
            </div>
          )}
          {["individual", "bundle"].includes(serviceType) &&
            !isCostZero &&
            (serviceValues.payment_term === "flexible" ||
              serviceValues.payment_term === "installment") && (
              <>
                <div className="bg-grayFlash flex w-[235px] rounded-[8px] self-start px-4 py-2">
                  Payment Options Available
                </div>
                <div className="mt-4">
                  <FlexPaymentsPreview
                    serviceValues={serviceValues}
                    serviceType={serviceType}
                  />
                </div>
              </>
            )}

          {["individual", "bundle"].includes(serviceType) &&
            !isCostZero &&
            serviceValues.payment_term === "full_pay" && (
              <>
                <div className="bg-grayMist my-[32px] h-[1px] w-full"></div>
                <div className="border-1 border-gray flex w-full flex-col rounded-[16px] border p-[20px]">
                  <p className="text-graySlate mb-[32px] text-base">
                    Payment Details
                  </p>
                  <div className="mb-[16px] flex w-full items-center justify-between">
                    <div className="flex min-w-fit items-center gap-[16px]">
                      <div className="bg-blurple flex h-[24px] w-[24px] items-center justify-center rounded-full font-bold text-white">
                        $
                      </div>
                      <p className="text-[16px]">Payment at checkout</p>
                    </div>
                    <div className="border-grayCloud mx-[24px] h-2 w-full -translate-y-1 transform border-b border-dashed"></div>
                    <span className="text-base">
                      {serviceValues.symbol &&
                        String.fromCharCode(
                          parseInt(serviceValues.symbol.slice(2), 16)
                        )}
                      {Number(paymentAmount || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="text-graySlate bg-grayMist flex w-full items-center justify-center rounded-[12px] p-[26px] text-base">
                    <p className="text-center">
                      Payment will be charged{" "}
                      <strong className="text-black">immediately</strong> at
                      checkout.
                    </p>
                  </div>
                </div>
              </>
            )}

          {serviceType === "recurring" && !isCostZero && (
            <div className="border-1 border-gray flex w-full flex-col rounded-[16px] border p-[20px]">
              <p className="text-graySlate mb-[32px] text-base">
                Payment Details
              </p>
              <div className="mb-[16px] flex w-full items-center justify-between">
                <div className="flex min-w-fit items-center gap-[16px]">
                  <SvgPiechartOneQuarter />
                  <p className="text-[16px]">Payment at booking</p>
                </div>
                <div className="border-grayCloud mx-[24px] h-2 w-full -translate-y-1 transform border-b border-dashed"></div>
                <span className="text-base">
                  {serviceValues.symbol &&
                    String.fromCharCode(
                      parseInt(serviceValues.symbol.slice(2), 16)
                    )}
                  {parseInt(serviceValues?.recurring?.periodCost)?.toFixed(2)}
                </span>
              </div>
              <div className="mb-[16px] flex w-full items-center justify-between">
                <div className="flex min-w-fit items-center gap-[16px]">
                  <SvgPiechartOneQuarter />
                  <p className="text-[16px]">
                    {serviceValues?.recurring.recurringFrequency === "weekly"
                      ? "Weekly"
                      : "Monthly"}{" "}
                    recurring charge
                  </p>
                </div>
                <div className="border-grayCloud mx-[24px] h-2 w-full -translate-y-1 transform border-b border-dashed"></div>
                <span className="text-base">
                  {serviceValues.symbol &&
                    String.fromCharCode(
                      parseInt(serviceValues.symbol.slice(2), 16)
                    )}
                  {parseInt(serviceValues?.recurring?.periodCost)?.toFixed(2)}
                </span>
              </div>
              <div className="text-graySlate bg-grayMist flex w-full items-center justify-center rounded-[12px] p-[26px] text-base">
                <p className="text-center">
                  Payments will be charged{" "}
                  <strong className="text-black">
                    {serviceValues?.recurring?.recurringFrequency}
                  </strong>{" "}
                  on the anniversary of the first payment.
                </p>
              </div>
            </div>
          )}
        </div>
        <div className="my-auto mt-[32px] flex flex-col items-center md:mt-0 md:w-[336px]">
          <SvgStarBox className="mb-[32px] hidden md:block" />
          <div className="mb-[48px] hidden w-[167px] text-center text-[22px] font-bold md:block">
            Everything look good?
          </div>
          <div className="mb:pb-0 absolute bottom-0 left-0 w-full bg-white px-[20px] pb-[64px] pt-[12px] shadow-lg md:static md:w-[80%] md:px-0 md:pb-0 md:shadow-none">
            <Button
              className="text-md mb-[16px] w-full"
              onClick={handleClick}
              size={"lg"}
            >
              {`${
                saveType === "create"
                  ? serviceValues.enabled
                    ? "Create"
                    : "Save"
                  : "Save"
              } ${serviceValues.enabled ? "Service" : "Draft"}`}
            </Button>
            <button
              className="btn-primary btn-secondary w-full gap-[10px]"
              onClick={() => setShowPreview(false)}
            >
              Back to Editing
              <SvgEdit />
            </button>
            {!isCostZero && (
              <div className="flex gap-[10px] mt-[32px]">
                <h4 className="text-base mb-[8px] mt[10px]">
                  Enable clients to use your promo codes?
                </h4>
                <div className="flex items-center gap-2">
                  <Switch
                    onCheckedChange={() =>
                      setServiceValues({
                        ...serviceValues,
                        allowPromo: !serviceValues.allowPromo,
                      })
                    }
                    checked={serviceValues.allowPromo}
                    id="promo"
                  />
                  <Label htmlFor="free">Yes</Label>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ServicePreview
